import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import apollo from "@/helpers/apollo";
import { cloudinaryImageUrl } from "@/helpers/cloudinary-url";
import { getUserHeadMeta } from "@/helpers/user-meta";
import Profile from "@/pages/user/profile";
import getProfileSchema from "@/pages/user/profile/get-schema-data";
import { SSR_QUERY } from "@/pages/user/profile/graphql";
import type { UserDataType, UserVariablesType } from "@/pages/user/types";

const fetchUserProfile = createServerFn({ method: "GET" })
  .inputValidator((data: { username: string }) => data)
  .handler(async ({ data }) => {
    const client = apollo();
    const { data: userData } = await client.query<
      UserDataType,
      UserVariablesType
    >({
      query: SSR_QUERY,
      variables: { isUser: false, username: data.username },
    });

    if (!userData.user) {
      throw notFound();
    }

    return {
      badges: userData.badges?.objects || [],
      badgesCount: userData.badges?.count || 0,
      user: userData.user,
    };
  });

export const Route = createFileRoute("/@{$username}/")({
  component: UserProfilePage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const { user } = loaderData;
    const imageUrl = user.imageId
      ? cloudinaryImageUrl(user.imageId)
      : undefined;
    const profileSchema = getProfileSchema(user, imageUrl);

    return {
      links: [
        {
          href: `${process.env.APP_URL}/@${user.username}`,
          rel: "canonical",
        },
      ],
      meta: [...getUserHeadMeta(user), { "script:ld+json": profileSchema }],
    };
  },
  loader: ({ params }) =>
    fetchUserProfile({ data: { username: params.username } }),
});

function UserProfilePage() {
  const data = Route.useLoaderData();
  return <Profile {...data} />;
}
